import { useState, useEffect, useRef } from "react";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Play, Coins, Gift, Zap, Clock, AlertCircle, X, Sparkles, TrendingUp } from "lucide-react";

const AD_DEFS = [
  { id: "short",  duration: 15, coins: 10, xp: 5,  daily_limit: 10, label_key: "shortAd",  color: "bg-[#00E5FF]/15 text-[#00E5FF]", videos: ["aqz-KE-bpKQ", "BHACKCNDMW8", "9No-FiEInLA"] },
  { id: "medium", duration: 30, coins: 25, xp: 12, daily_limit: 5,  label_key: "mediumAd", color: "bg-[#FF9500]/15 text-[#FF9500]", videos: ["jNQXAC9IVRw", "ScMzIvxBSi4", "fLexgOxsZu0"] },
  { id: "long",   duration: 60, coins: 50, xp: 25, daily_limit: 2,  label_key: "longAd",   color: "bg-[#D4AF37]/15 text-[#D4AF37]", videos: ["dQw4w9WgXcQ", "kJQP7kiw5Fk", "OPf0YbXqDm0"] },
];

const pickFrom = (list) => list[Math.floor(Math.random() * list.length)];

const fmtRemaining = (ms) => {
  if (ms <= 0) return "0m";
  const total_minutes = Math.ceil(ms / 60000);
  const h = Math.floor(total_minutes / 60);
  const m = total_minutes % 60;
  return h >= 1 ? `${h}h ${m}m` : `${m}m`;
};

// Extract YouTube ID from URL or assume already an ID
const ytId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : (url.length === 11 ? url : null);
};

// Extract TikTok video URL — TikTok embeds use /embed/v2/{id} format
const tiktokEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/tiktok\.com\/.+\/video\/(\d+)/) || url.match(/tiktok\.com\/v\/(\d+)/);
  return m ? `https://www.tiktok.com/embed/v2/${m[1]}` : null;
};

export default function Earn() {
  const { t, lang } = useLang();
  const { user, refreshUser } = useAuth();
  const [playing, setPlaying] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null); // { platform, src, sourceId }
  const [progress, setProgress] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [promoted, setPromoted] = useState({ items: [] });
  const [doubleOffer, setDoubleOffer] = useState(null); // { ad_type, coins, xp }
  const [doublePhase, setDoublePhase] = useState(null); // null | "watching"
  const timerRef = useRef(null);
  const longRotateIdx = useRef(0);

  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(i);
  }, []);

  // Load promoted/fallback feed on mount + every 60s
  useEffect(() => {
    const fetchFeed = () => api.get("/ads/promoted-feed").then((r) => setPromoted(r.data)).catch(() => {});
    fetchFeed();
    const i = setInterval(fetchFeed, 60000);
    return () => clearInterval(i);
  }, []);

  // Choose which video to play for a given ad slot based on priority rules
  const pickVideoForSlot = (adId) => {
    const items = promoted.items || [];
    if (items.length > 0) {
      let chosen = null;
      if (adId === "short") chosen = items[0];
      else if (adId === "medium") chosen = items[1] || items[0];
      else if (adId === "long") {
        // Rotate through all promoted (alternating YouTube/TikTok if both present)
        const yt = items.filter((x) => x.platform === "youtube");
        const tk = items.filter((x) => x.platform === "tiktok");
        if (yt.length && tk.length) {
          const useT = longRotateIdx.current % 2 === 1;
          chosen = useT ? tk[Math.floor(longRotateIdx.current / 2) % tk.length] : yt[Math.floor(longRotateIdx.current / 2) % yt.length];
        } else {
          chosen = items[longRotateIdx.current % items.length];
        }
        longRotateIdx.current++;
      }
      if (chosen) {
        if (chosen.platform === "tiktok") {
          const src = tiktokEmbed(chosen.url);
          if (src) return { platform: "tiktok", src, sourceId: chosen.id };
        } else {
          const id = ytId(chosen.url);
          if (id) return { platform: "youtube",
            src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1`,
            sourceId: chosen.id };
        }
      }
    }
    // Fallback to bundled defaults (last resort)
    const ad = AD_DEFS.find((a) => a.id === adId);
    const id = pickFrom(ad.videos);
    return { platform: "youtube",
      src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1`,
      sourceId: null };
  };

  const startWatching = (adId, isDoublePhase = false) => {
    setCurrentVideo(pickVideoForSlot(adId));
    setProgress(0);
    setPlaying(adId);
    if (isDoublePhase) setDoublePhase("watching");
  };

  // Watch timer — full duration enforced
  useEffect(() => {
    if (!playing) return;
    const ad = AD_DEFS.find((a) => a.id === playing);
    const step = 100 / (ad.duration * 5);
    timerRef.current = setInterval(() => {
      setProgress((p) => {
        const next = p + step;
        if (next >= 100) {
          clearInterval(timerRef.current);
          (async () => {
            try {
              const isDouble = doublePhase === "watching";
              const endpoint = isDouble ? "/ads/double-reward" : "/ads/reward";
              const { data } = await api.post(endpoint, { ad_type: playing });
              // Track view on the promoted/fallback video
              if (currentVideo?.sourceId) {
                api.post(`/ads/track-view/${currentVideo.sourceId}`).catch(() => {});
              }
              if (isDouble) {
                toast.success(`🚀 2X! +${data.coins_added} ${lang === "ar" ? "نقطة" : "coins"} +${data.xp_added} XP`);
                setDoublePhase(null);
              } else {
                toast.success(`+${data.coins_added} ${lang === "ar" ? "نقطة" : "coins"} +${data.xp_added} XP`);
                // Trigger Double Rewards popup
                setDoubleOffer({ ad_type: playing, coins: data.coins_added, xp: data.xp_added });
              }
              await refreshUser();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Failed");
              setDoublePhase(null);
            }
            setPlaying(null); setCurrentVideo(null); setProgress(0);
          })();
          return 100;
        }
        return next;
      });
    }, 200);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  const claimBase = () => setDoubleOffer(null);
  const acceptDouble = () => {
    const adType = doubleOffer.ad_type;
    setDoubleOffer(null);
    startWatching(adType, true);
  };

  const redeem = async () => {
    try { const { data } = await api.post("/ads/redeem");
      toast.success(`+$${data.credited_balance}`); await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const resetInfoFor = (adId) => {
    const adToday = user?.ads_today || {};
    const resetIso = adToday[`${adId}_reset_at`];
    if (!resetIso) return { msUntilReset: 0 };
    try {
      const resetAt = new Date(resetIso).getTime();
      const nextReset = resetAt + 24 * 60 * 60 * 1000;
      return { msUntilReset: Math.max(0, nextReset - now) };
    } catch { return { msUntilReset: 0 }; }
  };

  const ads = user?.ads_watched_total || 0;
  const used = user?.free_roulette_used || 0;
  const needed = 1000 * (used + 1);
  const rouletteProgress = Math.min(100, (ads / needed) * 100);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient mb-2">{t("earn")}</h1>
      <p className="text-sm text-[#8FA3BD] mb-2">{t("coinsRate")}</p>

      <div className="bento-card p-3 mb-6 border-[#00E5FF]/30 bg-[#00E5FF]/5 flex items-start gap-2.5">
        <Clock className="w-4 h-4 text-[#00E5FF] mt-0.5 shrink-0" />
        <p className="text-xs text-[#00E5FF]" data-testid="ads-reset-notice">
          🔄 تجدد مشاهدة اعلانات بعد <b>24 ساعة</b> من آخر اعلان قمت بمشاهدته
          <span className="text-[#8FA3BD]"> — Ads refresh 24h from your last view (per ad-type)</span>
        </p>
      </div>

      {/* Promoted-feed source indicator */}
      {promoted.items.length > 0 && (
        <div className="text-[10px] text-[#8FA3BD] mb-3 flex items-center gap-1.5" data-testid="promoted-feed-indicator">
          <TrendingUp className="w-3 h-3 text-[#D4AF37]" />
          {promoted.items.length} {lang === "ar" ? "إعلان ترويجي نشط" : "active promoted ad(s)"} • {lang === "ar" ? "المصدر" : "source"}: {promoted.items[0].source}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="bento-card p-5">
          <Coins className="w-5 h-5 text-[#00E5FF] mb-2" />
          <div className="text-2xl font-bold">{user?.coins || 0}</div>
          <div className="text-xs text-[#8FA3BD]">{t("coins")}</div>
        </div>
        <div className="bento-card p-5">
          <Zap className="w-5 h-5 text-[#FF9500] mb-2" />
          <div className="text-2xl font-bold">{(user?.xp || 0).toLocaleString()}</div>
          <div className="text-xs text-[#8FA3BD]">XP</div>
        </div>
        <div className="bento-card p-5">
          <Gift className="w-5 h-5 text-[#D4AF37] mb-2" />
          <div className="text-2xl font-bold">{ads}/{needed}</div>
          <div className="text-xs text-[#8FA3BD]">{t("adsNeeded")}</div>
          <div className="xp-bar mt-2"><div className="xp-bar-fill" style={{ width: `${rouletteProgress}%` }} /></div>
        </div>
      </div>

      <Button onClick={redeem} disabled={(user?.coins || 0) < 100} variant="outline" className="border-[#1A2D4D] text-white mb-8" data-testid="redeem-btn">
        <Gift className="w-4 h-4 me-1.5" /> {t("redeem")}
      </Button>

      <h2 className="text-lg font-semibold mb-4">{t("watchAd")}</h2>
      <div className="grid sm:grid-cols-3 gap-4">
        {AD_DEFS.map((ad) => {
          const active = playing === ad.id;
          const usedToday = user?.ads_today?.[ad.id] || 0;
          const remaining = ad.daily_limit - usedToday;
          const exhausted = remaining <= 0;
          const { msUntilReset } = resetInfoFor(ad.id);
          return (
            <div key={ad.id} className={`bento-card p-6 ${active ? "border-[#00E5FF]/40 ring-1 ring-[#00E5FF]/30" : ""}`} data-testid={`ad-card-${ad.id}`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs px-2 py-1 rounded-full ${ad.color} inline-flex items-center gap-1`}>
                  <Clock className="w-3 h-3" /> {ad.duration}s
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${exhausted ? "bg-red-500/15 text-red-400" : "bg-[#1A2D4D] text-[#8FA3BD]"}`} data-testid={`limit-${ad.id}`}>
                  {usedToday}/{ad.daily_limit}
                </span>
              </div>
              <div className="text-lg font-bold mt-3">{t(ad.label_key)}</div>
              <div className="text-sm text-[#8FA3BD] mt-1">+{ad.coins} {lang === "ar" ? "نقطة" : "coins"} • +{ad.xp} XP</div>

              {!active && !exhausted && (
                <div className="mt-2 text-[10px] text-[#FF9500] flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {lang === "ar" ? `يجب مشاهدة كامل ${ad.duration} ثانية للحصول على المكافأة` : `Must watch full ${ad.duration}s for reward`}
                </div>
              )}

              <div className="aspect-video rounded-lg bg-[#050E1A] border border-[#1A2D4D] my-4 flex items-center justify-center relative overflow-hidden">
                {active && currentVideo ? (
                  <>
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src={currentVideo.src}
                      title={`${currentVideo.platform} video`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid={`vid-iframe-${ad.id}`}
                    />
                    <div className="absolute inset-0 z-[5]" style={{ pointerEvents: "auto" }} />
                    <div className="absolute bottom-2 left-2 right-2 h-1.5 bg-[#1A2D4D] rounded-full overflow-hidden z-10">
                      <div className="h-full bg-[#00E5FF] transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="absolute top-2 right-2 z-10 text-[10px] bg-black/70 px-2 py-0.5 rounded text-[#00E5FF] font-mono">
                      {Math.ceil((ad.duration * (100 - progress)) / 100)}s
                    </div>
                    {doublePhase === "watching" && (
                      <div className="absolute top-2 left-2 z-10 text-[10px] bg-[#D4AF37]/80 text-[#050E1A] font-bold px-2 py-0.5 rounded">
                        🚀 2X BONUS
                      </div>
                    )}
                  </>
                ) : <Play className="w-12 h-12 text-[#00E5FF] opacity-80" />}
              </div>

              {exhausted && msUntilReset > 0 ? (
                <div className="w-full px-3 py-2 rounded-lg bg-[#1A2D4D] text-center text-xs" data-testid={`cooldown-${ad.id}`}>
                  <Clock className="w-3 h-3 inline-block me-1 text-[#FF9500]" />
                  <span className="text-[#FF9500] font-bold">{lang === "ar" ? "يتجدد بعد" : "Refreshes in"}: {fmtRemaining(msUntilReset)}</span>
                </div>
              ) : (
                <Button onClick={() => startWatching(ad.id)} disabled={!!playing || exhausted} className="btn-cyan w-full" data-testid={`watch-${ad.id}-btn`}>
                  {active ? `${Math.ceil((ad.duration * (100 - progress)) / 100)}s` : exhausted ? (lang === "ar" ? "وصلت الحد" : "Daily limit reached") : `+${ad.coins} • +${ad.xp} XP`}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Double Rewards Popup */}
      <Dialog open={!!doubleOffer} onOpenChange={(o) => !o && claimBase()}>
        <DialogContent className="bg-gradient-to-br from-[#0F1F35] to-[#0B1929] border-[#D4AF37]/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl text-gold-gradient flex items-center justify-center gap-2">
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
              {lang === "ar" ? "ضاعف مكافأتك!" : "Double all your rewards!"}
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] font-bold text-lg mb-4" data-testid="double-2x-badge">
              2X
            </div>
            <div className="text-sm text-[#8FA3BD] mb-2">{lang === "ar" ? "مكافأتك الحالية" : "Current reward"}</div>
            <div className="flex items-center justify-center gap-4 mb-3">
              <div className="bento-card p-3 min-w-[100px]">
                <Coins className="w-4 h-4 text-[#00E5FF] mx-auto mb-1" />
                <div className="text-xl font-bold text-[#00E5FF]" data-testid="double-base-coins">+{doubleOffer?.coins}</div>
                <div className="text-[10px] text-[#8FA3BD]">{lang === "ar" ? "نقطة" : "coins"}</div>
              </div>
              <div className="bento-card p-3 min-w-[100px]">
                <Zap className="w-4 h-4 text-[#FF9500] mx-auto mb-1" />
                <div className="text-xl font-bold text-[#FF9500]" data-testid="double-base-xp">+{doubleOffer?.xp}</div>
                <div className="text-[10px] text-[#8FA3BD]">XP</div>
              </div>
            </div>
            <div className="text-xs text-[#00E676] mb-4">
              {lang === "ar"
                ? `شاهد إعلاناً إضافياً واحصل على +${doubleOffer?.coins} نقطة و +${doubleOffer?.xp} XP إضافية (لا يحسب من حدّك اليومي)`
                : `Watch one more ad and get +${doubleOffer?.coins} coins & +${doubleOffer?.xp} XP extra (won't count against your daily limit)`}
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button onClick={claimBase} variant="outline" className="border-[#1A2D4D] text-white flex-1" data-testid="claim-base-btn">
              <X className="w-4 h-4 me-1.5" /> {lang === "ar" ? "احصل على المكافأة الأساسية" : "CLAIM"}
            </Button>
            <Button onClick={acceptDouble} className="btn-gold flex-1 font-bold" data-testid="accept-double-btn">
              <Sparkles className="w-4 h-4 me-1.5" /> {lang === "ar" ? "ضاعف المكافأة" : "DOUBLE REWARDS"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-10 bento-card p-6">
        <div className="text-xs text-[#4A5970] mb-1">Google AdSense placeholder</div>
        <div className="aspect-[6/1] bg-[#050E1A] border border-dashed border-[#1A2D4D] rounded-lg flex items-center justify-center text-[#4A5970] text-sm" data-testid="adsense-placeholder">
          728×90 Ad Slot
        </div>
      </div>
    </div>
  );
}
